import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  from,
  fromPromise,
} from "@apollo/client/core";

import VueApollo from "vue-apollo";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import store from "@/store/store";
import {
  attachReference,
  isInternalGraphQLError,
  referenceMessage,
  reportError,
} from "@/services/error-reporter";
import { getSupportEmail } from "@/config/app-config";

const AUTH_TOKEN = "luxweb";
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP;
export const filesRoot =
  process.env.VUE_APP_FILES_ROOT ||
  httpEndpoint.substr(0, httpEndpoint.indexOf("/graphql"));

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Rejected/expired access token: force-refresh via Amplify (single retry
  // per operation — the auth link below fetches the CURRENT token on every
  // attempt). If the refresh fails the session is cleared and the user lands
  // on the native /login page (never a loop).
  const statusCode = (networkError as any)?.statusCode;
  if (statusCode === 401 && !operation.getContext().retriedAfterRefresh) {
    return fromPromise(
      fetchAuthSession({ forceRefresh: true })
        .then((session) => !!session.tokens?.accessToken)
        .catch(() => false)
    ).flatMap((refreshed) => {
      if (refreshed) {
        operation.setContext({ retriedAfterRefresh: true });
        return forward(operation);
      }
      signOut().catch(() => {
        /* local cleanup is best-effort */
      });
      store.dispatch("setLoggedOut");
      if (window.location.pathname !== "/login") {
        window.location.assign(
          `/login?redirect=${encodeURIComponent(
            window.location.pathname + window.location.search
          )}`
        );
      }
      throw networkError;
    });
  }
  if (graphQLErrors?.length) {
    // Internal polling signal used by GSplat backend sampling flow.
    // This is expected and handled in-view, so don't show global error toast.
    const reportable = graphQLErrors.filter(
      ({ message }) =>
        !(typeof message === "string" && message.startsWith("SAMPLING_IN_PROGRESS:"))
    );

    // A view that renders the failure itself opts out of the toast, so one
    // problem is not announced twice in two places.
    const suppressed = operation.getContext().suppressGlobalError === true;
    const toast = (content: string) => {
      if (!suppressed) {
        store.commit("showMessage", { content, color: "red", timeout: 6000 });
      }
    };

    // Schema-level failures are OUR bug, not the user's, and the raw text is
    // both meaningless to them and a description of our API. `Cannot query
    // field "x" on type "Mutation"` means the deployed client is ahead of the
    // deployed backend.
    const internal = reportable.filter(({ extensions }) =>
      isInternalGraphQLError(extensions?.code as string | undefined)
    );

    if (internal.length) {
      // ONE reference for the whole operation, logged once and stamped onto
      // every error object Apollo will hand to the caller. Without this the
      // caller's catch mints a second id and the user is shown two references
      // for one failure, neither of which finds the whole story in CloudWatch.
      const reference = reportError(
        { graphQLErrors: reportable, networkError },
        `graphql ${operation.operationName || "operation"}`,
        store.getters?.user?.awsUserId
      );
      reportable.forEach((graphQLError) => attachReference(graphQLError, reference));
      attachReference(networkError, reference);
      toast(referenceMessage(reference, getSupportEmail()));
    }

    // Resolver messages are written to be read ("Forbidden: cross-organization
    // access is not allowed"), so they are shown as-is.
    reportable
      .filter(
        ({ extensions }) => !isInternalGraphQLError(extensions?.code as string | undefined)
      )
      .forEach(({ message }) => toast(message as string));
  }
});

const httpLink = new HttpLink({ uri: httpEndpoint, fetch });

// Attach the CURRENT Cognito access token on every request. fetchAuthSession
// refreshes transparently when the cached token is expired — no static
// localStorage token to go stale.
const authLink = setContext(async (_operation, { headers }) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  } catch (e) {
    return { headers: { ...headers, authorization: "" } };
  }
});

const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $query: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
  },
});

export async function setApolloAuthToken(client, token) {
  if (typeof localStorage !== "undefined" && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  if (client) {
    try {
    } catch (e) {
    }
  }
}

export async function removeApolloAuthToken(client) {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_TOKEN);
  }
  if (client) {
    try {
    } catch (e) {
    }
  }
}

export { apolloClient };
